import { requestGptJson } from './bid-ai-api';
import type { ParsedPdf } from './pdf-api-service';
import { projectMatchingService } from './project-matching-service';
import { vendorMatchingService } from './vendor-matching-service';
import { BID_CATEGORIES, type BidCategory } from '../constants';

export interface BidAnalysisResult {
  subcontractor_name: string;
  category: BidCategory;
  date: string;
  total_amount: number | null;
  /** The project the document names, as written in it (for display) */
  project_name?: string;
  /** The company's project that document matched, when one did */
  matched_project?: { id: string; name: string };
  /** The company's vendor that matched the extracted name, when one did */
  vendor_id?: string;
  vendor_match_confidence?: number;
}

const today = () => new Date().toISOString().split('T')[0];

function buildPrompt(text: string): string {
  return `You are a construction bid analysis AI. Analyze the following bid document and extract key information.

BID DOCUMENT:
${text}

EXTRACTION REQUIREMENTS:
1. **Subcontractor Name**: the company/vendor that issued the bid
2. **Category**: the most appropriate category from this list: ${BID_CATEGORIES.join(', ')}
3. **Date**: the bid or proposal date (format: YYYY-MM-DD)
4. **Total Amount**: the main bid total (number only, no currency symbols)
5. **Project Name**: the project the bid is for, if the document names it

RESPONSE FORMAT (JSON only):
{
  "subcontractor_name": "Company Name",
  "category": "Category from the list",
  "date": "YYYY-MM-DD",
  "total_amount": 123456.78,
  "project_name": "Project Name or null"
}

RULES:
- If you cannot find a piece of information, use null
- If no category fits, use "Other"
- total_amount is the overall bid total, not an individual line item
- Respond with ONLY the JSON object, no other text`;
}

function parseResponse(response: string, fallbackName: string): Omit<BidAnalysisResult, 'matched_project' | 'vendor_id' | 'vendor_match_confidence'> {
  let parsed: any;
  try {
    parsed = JSON.parse(response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim());
  } catch {
    throw new Error('The AI could not read this document. Please try again, or create the bid manually.');
  }
  const name = typeof parsed.subcontractor_name === 'string' ? parsed.subcontractor_name.trim() : '';
  const total = typeof parsed.total_amount === 'number' && Number.isFinite(parsed.total_amount) ? parsed.total_amount : null;
  return {
    // the document names no company: the file name is the best label we have, and the user can fix it in the form
    subcontractor_name: name || fallbackName,
    category: (BID_CATEGORIES as readonly string[]).includes(parsed.category) ? parsed.category : 'Other',
    date: /^\d{4}-\d{2}-\d{2}$/.test(parsed.date) ? parsed.date : today(),
    total_amount: total,
    project_name: typeof parsed.project_name === 'string' && parsed.project_name.trim() ? parsed.project_name.trim() : undefined,
  };
}

export const bidAnalysisService = {
  /** Reads a bid PDF: who issued it, what it covers, how much, and which of the company's project / vendor it is. */
  async analyzeBid(pdf: ParsedPdf): Promise<BidAnalysisResult> {
    const result: BidAnalysisResult = parseResponse(await requestGptJson(buildPrompt(pdf.text)), pdf.fileName);

    const [project, vendor] = await Promise.all([
      projectMatchingService.matchProject(pdf.text, result.project_name),
      vendorMatchingService.matchVendor(result.subcontractor_name),
    ]);
    if (project) result.matched_project = { id: project.id, name: project.name };
    if (vendor.matchedVendor) {
      result.vendor_id = vendor.matchedVendor.id;
      result.vendor_match_confidence = vendor.confidence;
    }
    return result;
  },
};
